import React from 'react';
import {
  ShieldCheck,
  User as UserIcon,
  Building,
  Hash,
  Calendar,
  Lock,
  Eye,
} from 'lucide-react';
import type { Book, User } from '../../../../shared/types/index.js';

interface CertificateSummaryTabProps {
  book: Book;
  user: User | null;
  fingerprint: string;
  copiedFingerprint: boolean;
  isPublic?: boolean;
  onCopyFingerprint: () => void;
}

export const CertificateSummaryTab: React.FC<CertificateSummaryTabProps> = ({
  book,
  user,
  fingerprint,
  copiedFingerprint,
  isPublic = false,
  onCopyFingerprint,
}) => {
  return (
    <div className="space-y-4 text-left select-none" onContextMenu={(e) => e.preventDefault()}>
      {/* Requester / Public Status Badge */}
      <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-between text-xs text-emerald-900">
        <div className="flex items-center gap-2">
          {user ? (
            <>
              <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0" />
              <span>
                Diterbitkan secara sah untuk pengguna terautentikasi: <strong>{user.name}</strong> (
                {user.role.toUpperCase()})
              </span>
            </>
          ) : (
            <>
              <Eye className="w-4 h-4 text-emerald-600 shrink-0" />
              <span>
                Sertifikat Resmi Terdaftar UIBN • <strong>Mode Lihat Saja Publik</strong>
              </span>
            </>
          )}
        </div>
        <span className="text-[11px] font-mono text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded font-bold">
          {user ? `UID: ${user.id.slice(0, 12)}` : 'Status: Sah & Aktif'}
        </span>
      </div>

      {/* Certificate Overview Card */}
      <div className="p-4 rounded-xl bg-gradient-to-br from-indigo-50/70 via-white to-amber-50/50 border border-indigo-100 space-y-3">
        <div className="flex items-center justify-between border-b border-indigo-100/60 pb-3">
          <div>
            <span className="text-[10px] font-bold text-indigo-600 uppercase tracking-widest block">
              Platform Katalog • Registrasi UIBN
            </span>
            <h4 className="text-lg font-bold text-slate-900">{book.title}</h4>
          </div>
          <span className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold border border-emerald-200">
            Status: Sah & Terdaftar
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
          <div className="flex items-start gap-2 text-slate-600">
            <UserIcon className="w-4 h-4 text-sky-600 shrink-0 mt-0.5" />
            <div>
              <span className="text-slate-400 block text-[10px] uppercase font-bold">Penulis</span>
              <strong className="text-slate-800 text-sm">{book.authorName}</strong>
            </div>
          </div>

          <div className="flex items-start gap-2 text-slate-600">
            <Building className="w-4 h-4 text-purple-600 shrink-0 mt-0.5" />
            <div>
              <span className="text-slate-400 block text-[10px] uppercase font-bold">Penerbit</span>
              <strong className="text-slate-800 text-sm">{book.publisherName}</strong>
            </div>
          </div>

          <div className="flex items-start gap-2 text-slate-600">
            <Hash className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <span className="text-slate-400 block text-[10px] uppercase font-bold">
                Nomor UIBN
              </span>
              <strong className="font-mono text-indigo-700 text-sm">{book.uibn}</strong>
            </div>
          </div>

          <div className="flex items-start gap-2 text-slate-600">
            <Calendar className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
            <div>
              <span className="text-slate-400 block text-[10px] uppercase font-bold">
                Tahun & Edisi
              </span>
              <span className="text-slate-800 font-medium">
                Tahun {book.publicationYear} • Edisi {book.edition || '1'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Cryptographic SHA-256 Audit Box */}
      <div className="p-3.5 rounded-xl bg-slate-900 text-white space-y-2">
        <div className="flex items-center justify-between text-xs">
          <span className="font-mono text-[10px] font-bold text-indigo-300 uppercase tracking-wider">
            SHA-256 Digital Fingerprint
          </span>
          {!isPublic ? (
            <button
              type="button"
              onClick={onCopyFingerprint}
              className="text-[10px] font-bold text-amber-300 hover:text-amber-200 cursor-pointer"
            >
              {copiedFingerprint ? 'Tersalin' : 'Salin Hash'}
            </button>
          ) : (
            <span className="text-[10px] font-bold text-amber-400 flex items-center gap-1">
              <Lock className="w-3 h-3" /> Salin Terkunci
            </span>
          )}
        </div>
        <p className="font-mono text-[11px] text-slate-300 break-all bg-slate-950 p-2.5 rounded-lg border border-slate-800 leading-relaxed">
          {fingerprint || 'e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855'}
        </p>
      </div>
    </div>
  );
};
